import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Filter, ChevronLeft, ChevronRight } from 'lucide-react';
import { TrendLineChart, RankingTable } from '../../../components/charts';
import {
  useGetTopPerformingSummaryQuery,
  useGetHistoricalKVAttendanceQuery,
} from '../../../services/attendanceApi';
import type { KVTrendData, RankingEntry, TimeSeriesPoint, Region } from '../../../types/analytics';
import {
  getOrderByField,
  transformKVDataToRankings,
  REGION_COLORS,
  type ComputedDateRange,
} from '../useAnalyticsData';

interface SchoolsTabProps {
  computedDateRange: ComputedDateRange;
  employeeApiType: 'regular' | 'contractual';
  regions: any[];
}

const SchoolsTab: React.FC<SchoolsTabProps> = ({
  computedDateRange,
  employeeApiType,
  regions,
}) => {
  const navigate = useNavigate();

  // ── Local state ──
  const [kvSchoolsPage, setKvSchoolsPage] = useState(1);
  const [selectedRegionId, setSelectedRegionId] = useState<number | null>(null);

  // ── Tab-specific RTK queries (only fire when this tab is mounted) ──

  // Top KV trends chart data
  const { data: topKVSummaryData } = useGetTopPerformingSummaryQuery(
    {
      fromDate: computedDateRange.fromDateStr,
      toDate: computedDateRange.toDateStr,
      flag: 'kv',
      limit: 10,
      order: 'DESC',
    },
    { skip: !computedDateRange.isValid },
  );

  // Paginated KV list (also serves as page-1 top data — eliminates duplicate query)
  const { data: historicalKVDataTop, isLoading: isKVTopLoading } = useGetHistoricalKVAttendanceQuery(
    {
      page: kvSchoolsPage,
      fromDate: computedDateRange.fromDateStr,
      toDate: computedDateRange.toDateStr,
      orderBy: getOrderByField(employeeApiType),
      order: 'DESC',
    },
    { skip: !computedDateRange.isValid },
  );

  // Bottom KVs (needs improvement)
  const { data: bottomKVsData } = useGetHistoricalKVAttendanceQuery(
    {
      page: 1,
      fromDate: computedDateRange.fromDateStr,
      toDate: computedDateRange.toDateStr,
      orderBy: getOrderByField(employeeApiType),
      order: 'ASC',
    },
    { skip: !computedDateRange.isValid },
  );

  // ── Derived: top 10 KVs from page-1 data (reuse — no separate query!) ──
  const topPerformingKVs = useMemo(() => {
    if (kvSchoolsPage === 1 && historicalKVDataTop?.data) {
      return transformKVDataToRankings(historicalKVDataTop.data.slice(0, 10), 1, employeeApiType);
    }
    return [];
  }, [historicalKVDataTop, kvSchoolsPage, employeeApiType]);

  const needsImprovementKVs = useMemo(() => {
    if (!bottomKVsData?.data) return [];
    return transformKVDataToRankings(bottomKVsData.data.slice(0, 10), 1, employeeApiType);
  }, [bottomKVsData, employeeApiType]);

  // Paginated list for full table
  const kvSchoolsData = useMemo(() => {
    if (!historicalKVDataTop?.data) return [];
    return transformKVDataToRankings(
      historicalKVDataTop.data,
      (kvSchoolsPage - 1) * 50 + 1,
      employeeApiType,
    );
  }, [historicalKVDataTop, kvSchoolsPage, employeeApiType]);

  // Top KV trend chart data
  const topKVTrendsData = useMemo((): KVTrendData[] => {
    if (!topKVSummaryData?.data) return [];
    const dateGroups = topKVSummaryData.data;
    const kvMap = new Map<number, { kvId: number; kvName: string; roName: string; records: any[] }>();

    Object.keys(dateGroups).forEach(dateKey => {
      dateGroups[dateKey].forEach((record: any) => {
        const kvId = record.schoolID;
        if (!kvMap.has(kvId)) {
          kvMap.set(kvId, { kvId, kvName: record.schoolName, roName: record.roName, records: [] });
        }
        kvMap.get(kvId)!.records.push(record);
      });
    });

    return Array.from(kvMap.values()).map((kv, index) => {
      const sorted = kv.records.sort(
        (a: any, b: any) => new Date(a.attendanceDate).getTime() - new Date(b.attendanceDate).getTime(),
      );

      const timeSeries: TimeSeriesPoint[] = sorted.map((r: any) => {
        const regTotal = parseInt(r.totalEnrollmentReg) || 0;
        const conTotal = parseInt(r.totalEnrollmentCon) || 0;
        const regPercent = parseFloat(r.presentPercentageReg) || 0;
        const conPercent = parseFloat(r.presentPercentageCon) || 0;
        const regPresent = Math.round((regTotal * regPercent) / 100);
        const conPresent = Math.round((conTotal * conPercent) / 100);
        const overallPercent =
          regTotal + conTotal > 0 ? ((regPresent + conPresent) / (regTotal + conTotal)) * 100 : 0;

        return {
          date: new Date(r.attendanceDate).toISOString().split('T')[0],
          timestamp: new Date(r.attendanceDate).getTime(),
          regularPresent: regPresent,
          contractualPresent: conPresent,
          totalPresent: regPresent + conPresent,
          regularTotal: regTotal,
          contractualTotal: conTotal,
          totalStaff: regTotal + conTotal,
          regularPercent: regPercent,
          contractualPercent: conPercent,
          overallPercent,
        };
      });

      const avgReg =
        timeSeries.length > 0 ? timeSeries.reduce((s, p) => s + p.regularPercent, 0) / timeSeries.length : 0;
      const avgCon =
        timeSeries.length > 0
          ? timeSeries.reduce((s, p) => s + p.contractualPercent, 0) / timeSeries.length
          : 0;

      const mid = Math.floor(timeSeries.length / 2);
      const pick = (p: TimeSeriesPoint) =>
        employeeApiType === 'contractual' ? p.contractualPercent : p.regularPercent;
      const fAvg = mid > 0 ? timeSeries.slice(0, mid).reduce((s, p) => s + pick(p), 0) / mid : 0;
      const sAvg =
        timeSeries.length - mid > 0
          ? timeSeries.slice(mid).reduce((s, p) => s + pick(p), 0) / (timeSeries.length - mid)
          : 0;
      const trendPercent = Math.round((sAvg - fAvg) * 10) / 10;
      const trendDirection: 'up' | 'down' | 'stable' =
        trendPercent > 1 ? 'up' : trendPercent < -1 ? 'down' : 'stable';

      return {
        kvId: kv.kvId,
        kvName: kv.kvName,
        roName: kv.roName,
        stateName: '',
        timeSeries,
        averageAttendance: Math.round((employeeApiType === 'contractual' ? avgCon : avgReg) * 10) / 10,
        trendDirection,
        trendPercent,
        rank: index + 1,
      };
    });
  }, [topKVSummaryData, employeeApiType]);

  // ── Handlers ──
  const handleKVClick = (entry: RankingEntry) => {
    const params = new URLSearchParams();
    params.append('fromDate', computedDateRange.fromDateStr);
    params.append('toDate', computedDateRange.toDateStr);
    navigate(`/attendance/analytics/kv/${entry.id}?${params.toString()}`);
  };

  const getModeLabel = () => (employeeApiType === 'contractual' ? 'Contractual Staff' : 'Regular Staff');

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Filter by Region:</span>
          </div>
          <select
            value={selectedRegionId ?? ''}
            onChange={e => setSelectedRegionId(e.target.value ? Number(e.target.value) : null)}
            className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500"
          >
            <option value="">All Regions</option>
            {regions.map((region: Region) => (
              <option key={region.roId} value={region.roId}>
                {region.roName}
              </option>
            ))}
          </select>
          <div className="ml-auto flex items-center gap-2">
            <span className="text-xs text-gray-500">Showing:</span>
            <span
              className={`px-3 py-1 text-sm font-medium rounded-full ${
                employeeApiType === 'contractual'
                  ? 'bg-orange-100 text-orange-700'
                  : 'bg-green-100 text-green-700'
              }`}
            >
              {getModeLabel()}
            </span>
          </div>
        </div>
      </div>

      {/* KV Trend Chart */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Top 10 KV Schools - {getModeLabel()} Attendance Trend
        </h3>
        <TrendLineChart
          data={topKVTrendsData}
          height={400}
          showLegend={true}
          showArea={false}
          compareMode={employeeApiType}
        />
      </div>

      {/* Top/Bottom KVs */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <RankingTable
          data={topPerformingKVs}
          title={`🏆 Top Performing KVs (${getModeLabel()})`}
          limit={10}
          highlightTop={3}
          onRowClick={handleKVClick}
        />
        <RankingTable
          data={needsImprovementKVs.filter(kv => kv.attendancePercent < 80)}
          title={`⚠️ Needs Improvement (${getModeLabel()}) - Below 80%`}
          limit={10}
          highlightBottom={10}
          onRowClick={handleKVClick}
        />
      </div>

      {/* Full Table with Pagination */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">All KV Schools</h3>
          <div className="flex items-center gap-2 text-sm text-gray-500">Page {kvSchoolsPage}</div>
        </div>

        {isKVTopLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          </div>
        ) : (
          <RankingTable data={kvSchoolsData} limit={50} highlightTop={0} onRowClick={handleKVClick} />
        )}

        {/* Pagination Controls */}
        <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-center bg-gray-50">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setKvSchoolsPage(prev => Math.max(1, prev - 1))}
              disabled={kvSchoolsPage === 1 || isKVTopLoading}
              className="p-2 rounded-lg border border-gray-200 hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title="Previous page"
              aria-label="Previous page"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <div className="text-sm text-gray-600">
              Page <span className="font-medium">{kvSchoolsPage}</span>
            </div>
            <button
              onClick={() => setKvSchoolsPage(prev => prev + 1)}
              disabled={isKVTopLoading}
              className="p-2 rounded-lg border border-gray-200 hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title="Next page"
              aria-label="Next page"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(SchoolsTab);
